import { useEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { WithEnterpriseId } from '@mews-commander/types';
import {
    DateService,
    DateTimeService,
    lt,
    useAppClockService,
    useDialog,
    useFeatureFlag,
    useTimezone,
} from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import {
    Banner,
    Button,
    Card,
    Container,
    Dialog,
    Link,
    SimpleList,
    SimpleListItem,
    Stack,
    Typography,
} from '@optimus-web/core';

export const PropertyTimeline = ({ enterpriseId }: WithEnterpriseId) => {
    const appClockService = useAppClockService();
    const timezone = useTimezone();
    const enterprise = useEnterprise(enterpriseId);
    const nowDateTime = DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone);
    const nowDate = DateTimeService.toDate(nowDateTime);
    const createdDateTime = DateTimeService.fromUtcString(enterprise.createdUtc, timezone);
    const createdDate = DateTimeService.toDate(createdDateTime);
    const days = DateService.diffDates(nowDate, createdDate);
    const [{ onClose, active }, toggleDialog] = useDialog();
    const onboardingAuditFinancialIncentiveEnabled = useFeatureFlag(
        FeatureFlag.onboardingAuditFinancialIncentiveEnabled
    );

    if (enterprise.isOnboardingFinished) return null;

    return (
        <Card>
            <Container padding="300">
                <Stack spacing="200" vertical>
                    <Stack vertical>
                        <Typography textStyle="bodyLargeStrong" as="div">
                            {lt('Timeline')}
                        </Typography>
                        <Typography textStyle="bodySmall" variant="tertiary" as="div">
                            {/* Show the current days in onboarding, the first day is 0 + 1 */}
                            {lt('OnboardingAuditTimelineDaysInOnboarding', { count: days + 1 })}
                        </Typography>
                    </Stack>
                    {onboardingAuditFinancialIncentiveEnabled ? (
                        <>
                            <Banner
                                secondaryText={
                                    <Stack spacing="100" vertical>
                                        {lt('OnboardingAuditTimelineBannerText', { count: 21 })}
                                        <Typography as="div" textStyle="bodyMediumStrong">
                                            <Link onClick={toggleDialog} variant="basic">
                                                {lt('OnboardingAuditTimelineBannerLink')}
                                            </Link>
                                        </Typography>
                                    </Stack>
                                }
                                variant="info"
                            />
                            <Dialog
                                active={active}
                                onClose={onClose}
                                size="small"
                                title={lt('OnboardingAuditTimelineDialogTitle')}
                                footer={{
                                    primary: (
                                        <Button type="button" variant="primary" size="medium" onClick={onClose}>
                                            {lt('Ok')}
                                        </Button>
                                    ),
                                }}
                            >
                                <Stack spacing="400" vertical>
                                    {lt('OnboardingAuditTimelineDialogSubtitle')}
                                    <SimpleList ordered={false} spacing="300">
                                        <SimpleListItem>
                                            {lt('OnboardingAuditTimelineDialogFirstListItem')}
                                        </SimpleListItem>
                                        <SimpleListItem>
                                            {lt('OnboardingAuditTimelineDialogSecondListItem')}
                                        </SimpleListItem>
                                        <SimpleListItem>
                                            {lt('OnboardingAuditTimelineDialogThirdListItem')}
                                        </SimpleListItem>
                                        <SimpleListItem>
                                            {lt('OnboardingAuditTimelineDialogFourthListItem', {
                                                link: (text) => (
                                                    <Link
                                                        href="https://www.mews.com/en/mews-inner-circle"
                                                        rel="noreferrer"
                                                        target="_blank"
                                                        variant="basic"
                                                    >
                                                        {text}
                                                    </Link>
                                                ),
                                            })}
                                        </SimpleListItem>
                                    </SimpleList>
                                    {lt('OnboardingAuditTimelineDialogNote')}
                                </Stack>
                            </Dialog>
                        </>
                    ) : null}
                </Stack>
            </Container>
        </Card>
    );
};
